import { AxiosInstance } from 'axios';
import { getApiBaseUrl, resolveApiFileUrl } from '../../utils/apiFileUrl';
import {
  CreateProjectRelatedItemInput,
  ProjectDocument,
  ProjectDocumentFolder,
  ProjectDocumentAuditEvent,
  ProjectDocumentCategory,
  ProjectDocumentUploadInput,
  ProjectDocumentUploadSession,
  ProjectDocumentVersion,
  ProjectDocumentVersionUploadInput,
  ProjectDrawing,
  ProjectDrawingAnnotation,
  ProjectDrawingAnnotationAttachment,
  ProjectDrawingAnnotationAttachmentUploadSession,
  ProjectDrawingAnnotationInput,
  ProjectDrawingAnnotationMessage,
  ProjectDrawingAnnotationReviewDecision,
  ProjectDrawingAnnotationThread,
  ProjectDrawingClassificationInput,
  ProjectDrawingDelivery,
  ProjectDrawingRevision,
  ProjectDrawingSet,
  ProjectDrawingSetPage,
  ProjectDrawingSetUploadInput,
  ProjectDrawingUploadSession,
  ProjectDrawingViewerBootstrap,
  ProjectDrawingMarkup,
  ProjectDrawingMarkupInput,
  ProjectDrawingOfflineDelivery,
  ProjectDrawingOfflineManifest,
  ProjectRfi,
  ProjectRfiAttachment,
  ProjectRfiAttachmentRole,
  ProjectRfiAttachmentUploadSession,
  ProjectRfiAssignableAdmin,
  ProjectRfiDraftInput,
  ProjectRfiManagementInput,
  ProjectRfiStatus,
  ProjectCoordinationEntityType,
  ProjectRelatedItemCandidate,
  ProjectRelatedItem,
} from '../../types';

const PROJECT_COORDINATION_API_BASE_URL = getApiBaseUrl();

export function coordinationContentUrl(path: string) {
  return `${PROJECT_COORDINATION_API_BASE_URL.replace(/\/$/, '')}${path}`;
}

export interface ProjectCoordinationApi {
  searchRelatedItemCandidates(
    projectId: number,
    input: {
      source_entity_type: ProjectCoordinationEntityType;
      source_entity_id: number;
      q: string;
      entity_type?: ProjectCoordinationEntityType;
    },
  ): Promise<ProjectRelatedItemCandidate[]>;
  getRelatedItems(
    projectId: number,
    entityType: ProjectCoordinationEntityType,
    entityId: number,
  ): Promise<ProjectRelatedItem[]>;
  createRelatedItem(projectId: number, input: CreateProjectRelatedItemInput): Promise<ProjectRelatedItem>;
  deleteRelatedItem(projectId: number, relationId: number): Promise<void>;
  getDocumentCategories(projectId: number): Promise<ProjectDocumentCategory[]>;
  updateDocumentCategory(
    projectId: number,
    categoryId: number,
    input: { is_enabled: boolean; sort_order: number },
  ): Promise<ProjectDocumentCategory>;
  reorderDocumentCategories(projectId: number, categoryIds: number[]): Promise<ProjectDocumentCategory[]>;
  getDocumentFolders(
    projectId: number,
    options?: { include_archived?: boolean },
  ): Promise<{ folders: ProjectDocumentFolder[]; unfiled_count: number; folder_access_mode: 'all' | 'restricted' }>;
  createDocumentFolder(projectId: number, name: string): Promise<ProjectDocumentFolder>;
  updateDocumentFolder(
    projectId: number,
    folderId: number,
    input: { name?: string; is_archived?: boolean },
  ): Promise<ProjectDocumentFolder>;
  getDocuments(
    projectId: number,
    filters?: { status?: 'active' | 'archived' | 'all'; category_id?: number; folder_id?: number | 'none'; search?: string },
  ): Promise<ProjectDocument[]>;
  getDocument(
    projectId: number,
    documentId: number,
  ): Promise<{ document: ProjectDocument; versions: ProjectDocumentVersion[] }>;
  getDocumentHistory(projectId: number, documentId: number): Promise<ProjectDocumentAuditEvent[]>;
  startDocumentUpload(
    projectId: number,
    input: ProjectDocumentUploadInput,
  ): Promise<ProjectDocumentUploadSession>;
  startDocumentVersionUpload(
    projectId: number,
    documentId: number,
    input: ProjectDocumentVersionUploadInput,
  ): Promise<ProjectDocumentUploadSession>;
  uploadDocumentChunk(
    projectId: number,
    token: string,
    offset: number,
    chunk: Blob,
    isFinal: boolean,
  ): Promise<{ next_offset?: number; document?: ProjectDocument; version_id?: number }>;
  updateDocument(
    projectId: number,
    documentId: number,
    input: Partial<Pick<ProjectDocument, 'category_id' | 'title' | 'description' | 'tags' | 'content_classification'>> & { folder_id?: number },
  ): Promise<ProjectDocument>;
  setDocumentArchived(projectId: number, documentId: number, archived: boolean): Promise<ProjectDocument>;
  openDocumentVersion(
    projectId: number,
    documentId: number,
    versionId: number,
  ): Promise<{ url: string; file_name: string; content_type: string; file_size: number; version_number: number }>;
  getDrawingSets(
    projectId: number,
    filters?: { status?: 'active' | 'archived' | 'all'; search?: string },
  ): Promise<ProjectDrawingSet[]>;
  getDrawingSet(projectId: number, setId: number): Promise<{ drawingSet: ProjectDrawingSet; pages: ProjectDrawingSetPage[] }>;
  openDrawingSet(projectId: number, setId: number): Promise<{
    url: string;
    viewer_url: string;
    drawing_set_id: number;
    page_count: number;
    file_name: string;
    content_type: string;
    file_size: number;
  }>;
  getDrawingSetDelivery(
    projectId: number,
    setId: number,
    options?: { signal?: AbortSignal },
  ): Promise<ProjectDrawingDelivery>;
  getDrawingSetHistory(projectId: number, setId: number): Promise<ProjectDocumentAuditEvent[]>;
  startDrawingSetUpload(projectId: number, input: ProjectDrawingSetUploadInput): Promise<ProjectDrawingUploadSession>;
  uploadDrawingSetChunk(
    projectId: number,
    token: string,
    offset: number,
    chunk: Blob,
    isFinal: boolean,
  ): Promise<{ next_offset?: number; drawing_set?: ProjectDrawingSet }>;
  setDrawingSetArchived(projectId: number, setId: number, archived: boolean): Promise<ProjectDrawingSet>;
  publishDrawingSetSource(projectId: number, setId: number): Promise<ProjectDrawingSet>;
  deleteDrawingSetPermanently(projectId: number, setId: number): Promise<{
    deleted: true;
    drawing_set_id: number;
    deleted_revision_count: number;
    deleted_drawing_count: number;
    preserved_drawing_count: number;
    deleted_annotation_count: number;
    storage_cleanup_failed: number;
  }>;
  setDrawingSetPageSkipped(projectId: number, setId: number, pageNumber: number, skipped: boolean): Promise<ProjectDrawingSetPage>;
  classifyDrawingSetPage(
    projectId: number,
    setId: number,
    pageNumber: number,
    input: ProjectDrawingClassificationInput,
  ): Promise<{ drawing: ProjectDrawing; revision: ProjectDrawingRevision }>;
  reviewDrawingSetPageMatch(projectId: number, setId: number, pageNumber: number, decision: 'confirm' | 'reject'): Promise<ProjectDrawingSetPage>;
  recognizeDrawingSetPage(projectId: number, setId: number, pageNumber: number, image: Blob, signal?: AbortSignal): Promise<{ text: string; confidence: number }>;
  batchDrawingSetPages(projectId: number, setId: number, input: { action: 'classify' | 'confirm' | 'publish' | 'skip' | 'restore'; items: { page_number: number; classification?: ProjectDrawingClassificationInput }[] }): Promise<{ results: { page_number: number; status: 'success' | 'processing' | 'error'; error?: string }[] }>;
  getDrawings(
    projectId: number,
    filters?: { status?: 'active' | 'archived' | 'all'; discipline?: string; search?: string; drawing_set_id?: number },
    options?: { signal?: AbortSignal },
  ): Promise<ProjectDrawing[]>;
  getDrawing(projectId: number, drawingId: number): Promise<{ drawing: ProjectDrawing; revisions: ProjectDrawingRevision[] }>;
  getDrawingHistory(projectId: number, drawingId: number): Promise<ProjectDocumentAuditEvent[]>;
  getDrawingViewerBootstrap(
    projectId: number,
    drawingId: number,
    revisionId?: number,
    options?: { signal?: AbortSignal },
  ): Promise<ProjectDrawingViewerBootstrap>;
  getDrawingRevisionDelivery(
    projectId: number,
    drawingId: number,
    revisionId: number,
    preferDerived?: boolean,
    options?: { signal?: AbortSignal },
  ): Promise<ProjectDrawingDelivery>;
  getDrawingOfflineManifest(
    projectId: number,
    drawingId: number,
    revisionId: number,
  ): Promise<ProjectDrawingOfflineManifest>;
  listDrawingOfflineManifest(
    projectId: number,
    drawingSetId?: number,
  ): Promise<ProjectDrawingOfflineManifest>;
  getDrawingOfflineDelivery(
    projectId: number,
    drawingId: number,
    revisionId: number,
    options?: { signal?: AbortSignal },
  ): Promise<ProjectDrawingOfflineDelivery>;
  getDrawingRevisionThumbnail(
    projectId: number,
    drawingId: number,
    revisionId: number,
    options?: { signal?: AbortSignal },
  ): Promise<Blob>;
  optimizeDrawingRevision(
    projectId: number,
    drawingId: number,
    revisionId: number,
  ): Promise<{ status: 'available' | 'generated' | 'processing' | 'unsupported'; file?: ProjectDrawingViewerBootstrap['file'] }>;
  uploadDrawingRevisionThumbnail(
    projectId: number,
    drawingId: number,
    revisionId: number,
    thumbnail: Blob,
  ): Promise<{ status: 'available' | 'processing' }>;
  setDrawingArchived(projectId: number, drawingId: number, archived: boolean): Promise<ProjectDrawing>;
  deleteDrawingPermanently(projectId: number, drawingId: number): Promise<{
    deleted: true;
    drawing_id: number;
    deleted_revision_count: number;
    deleted_annotation_count: number;
    storage_cleanup_failed: number;
  }>;
  publishDrawingRevision(
    projectId: number,
    drawingId: number,
    revisionId: number,
  ): Promise<{ status: 'processing'; retry_after_seconds: number } | { drawing: ProjectDrawing; revision: ProjectDrawingRevision; carriedForwardPinCount: number; carriedForwardMarkupCount: number }>;
  openDrawingRevision(
    projectId: number,
    drawingId: number,
    revisionId: number,
    preferDerived?: boolean,
  ): Promise<{
    url: string;
    viewer_url: string;
    revision_id: number;
    revision_code: string;
    page_number: number;
    is_derived: boolean;
    file_name: string;
    content_type: string;
    file_size: number;
  }>;
  getDrawingAnnotations(projectId: number, revisionId: number): Promise<ProjectDrawingAnnotation[]>;
  createDrawingAnnotation(
    projectId: number,
    revisionId: number,
    input: ProjectDrawingAnnotationInput,
  ): Promise<ProjectDrawingAnnotation>;
  updateDrawingAnnotation(
    projectId: number,
    revisionId: number,
    annotationId: number,
    input: Partial<ProjectDrawingAnnotationInput>,
  ): Promise<ProjectDrawingAnnotation>;
  deleteDrawingAnnotation(projectId: number, revisionId: number, annotationId: number): Promise<void>;
  setDrawingAnnotationStatus(projectId: number, revisionId: number, annotationId: number, status: 'open' | 'resolved'): Promise<ProjectDrawingAnnotation>;
  reviewDrawingAnnotation(projectId: number, revisionId: number, annotationId: number, decision: ProjectDrawingAnnotationReviewDecision, point?: { x_ratio: number; y_ratio: number }): Promise<ProjectDrawingAnnotation>;
  getDrawingAnnotationThread(projectId: number, revisionId: number, annotationId: number): Promise<ProjectDrawingAnnotationThread>;
  createDrawingAnnotationMessage(projectId: number, revisionId: number, annotationId: number, body: string): Promise<ProjectDrawingAnnotationMessage>;
  startDrawingAnnotationAttachmentUpload(projectId: number, revisionId: number, annotationId: number, input: { file_name: string; content_type: string; file_size: number }): Promise<ProjectDrawingAnnotationAttachmentUploadSession>;
  uploadDrawingAnnotationAttachmentChunk(projectId: number, revisionId: number, annotationId: number, token: string, offset: number, chunk: Blob, isFinal: boolean): Promise<{ next_offset?: number; attachment?: ProjectDrawingAnnotationAttachment }>;
  openDrawingAnnotationAttachment(projectId: number, revisionId: number, annotationId: number, attachmentId: number): Promise<{ url: string; file_name: string; content_type: string; file_size: number }>;
  deleteDrawingAnnotationAttachment(projectId: number, revisionId: number, annotationId: number, attachmentId: number): Promise<void>;
  getDrawingMarkups(projectId: number, revisionId: number): Promise<ProjectDrawingMarkup[]>;
  createDrawingMarkup(projectId: number, revisionId: number, input: ProjectDrawingMarkupInput): Promise<ProjectDrawingMarkup>;
  updateDrawingMarkup(projectId: number, revisionId: number, markupId: number, input: Partial<Pick<ProjectDrawingMarkupInput, 'geometry' | 'style' | 'annotation_id' | 'visibility'>> & { expected_updated_at: string }): Promise<ProjectDrawingMarkup>;
  deleteDrawingMarkup(projectId: number, revisionId: number, markupId: number, expectedUpdatedAt: string): Promise<void>;
  postDrawingViewerTelemetry(events: unknown[]): Promise<void>;
  getRfis(
    projectId: number,
    filters?: { status?: ProjectRfiStatus | 'all'; assignee_id?: number; search?: string },
  ): Promise<ProjectRfi[]>;
  getRfi(projectId: number, rfiId: number): Promise<{ rfi: ProjectRfi; history: ProjectDocumentAuditEvent[]; attachments: ProjectRfiAttachment[] }>;
  getRfiAssignableAdmins(projectId: number): Promise<ProjectRfiAssignableAdmin[]>;
  createRfiDraft(projectId: number, input: ProjectRfiDraftInput): Promise<ProjectRfi>;
  updateRfiDraft(projectId: number, rfiId: number, input: Partial<ProjectRfiDraftInput>): Promise<ProjectRfi>;
  updateRfiManagement(projectId: number, rfiId: number, input: ProjectRfiManagementInput): Promise<ProjectRfi>;
  openRfi(projectId: number, rfiId: number): Promise<ProjectRfi>;
  answerRfi(projectId: number, rfiId: number, response: string): Promise<ProjectRfi>;
  closeRfi(projectId: number, rfiId: number): Promise<ProjectRfi>;
  reopenRfi(projectId: number, rfiId: number): Promise<ProjectRfi>;
  voidRfi(projectId: number, rfiId: number, reason: string): Promise<ProjectRfi>;
  deleteRfiDraft(projectId: number, rfiId: number): Promise<void>;
  startRfiAttachmentUpload(
    projectId: number,
    rfiId: number,
    input: { content_classification?: 'operational' | 'financial'; attachment_role: ProjectRfiAttachmentRole; file_name: string; content_type: string; file_size: number },
  ): Promise<ProjectRfiAttachmentUploadSession>;
  uploadRfiAttachmentChunk(
    projectId: number,
    rfiId: number,
    token: string,
    offset: number,
    chunk: Blob,
    isFinal: boolean,
  ): Promise<{ next_offset?: number; attachment?: ProjectRfiAttachment }>;
  openRfiAttachment(
    projectId: number,
    rfiId: number,
    attachmentId: number,
  ): Promise<{ url: string; file_name: string; content_type: string; file_size: number; attachment_role: ProjectRfiAttachmentRole }>;
  deleteRfiAttachment(projectId: number, rfiId: number, attachmentId: number, role: ProjectRfiAttachmentRole): Promise<void>;
}

export function createProjectCoordinationApi(client: AxiosInstance): ProjectCoordinationApi {
  return {
    async searchRelatedItemCandidates(projectId, input) {
      const response = await client.get(`/projects/${projectId}/related-items/candidates`, {
        params: input,
      });
      return response.data.items;
    },
    async getRelatedItems(projectId, entityType, entityId) {
      const response = await client.get(
        `/projects/${projectId}/related-items/${encodeURIComponent(entityType)}/${entityId}`,
      );
      return response.data.items;
    },
    async createRelatedItem(projectId, input) {
      const response = await client.post(`/projects/${projectId}/related-items`, input);
      return response.data;
    },
    async deleteRelatedItem(projectId, relationId) {
      await client.delete(`/projects/${projectId}/related-items/${relationId}`);
    },
    async getDocumentCategories(projectId) {
      const response = await client.get(`/projects/${projectId}/document-categories`);
      return response.data.categories;
    },
    async updateDocumentCategory(projectId, categoryId, input) {
      const response = await client.patch(
        `/projects/${projectId}/document-categories/${categoryId}`,
        input,
      );
      return response.data.setting;
    },
    async reorderDocumentCategories(projectId, categoryIds) {
      const response = await client.put(`/projects/${projectId}/document-categories/order`, {
        category_ids: categoryIds,
      });
      return response.data.categories;
    },
    async getDocumentFolders(projectId, options) {
      const response = await client.get(`/projects/${projectId}/document-folders`, {
        params: options?.include_archived ? { include_archived: '1' } : undefined,
      });
      return {
        folders: response.data.folders,
        unfiled_count: Number(response.data.unfiled_count || 0),
        folder_access_mode: response.data.folder_access_mode === 'restricted' ? 'restricted' : 'all',
      };
    },
    async createDocumentFolder(projectId, name) {
      const response = await client.post(`/projects/${projectId}/document-folders`, { name });
      return response.data.folder;
    },
    async updateDocumentFolder(projectId, folderId, input) {
      const response = await client.patch(`/projects/${projectId}/document-folders/${folderId}`, input);
      return response.data.folder;
    },
    async getDocuments(projectId, filters) {
      const response = await client.get(`/projects/${projectId}/documents`, { params: filters });
      return response.data.documents;
    },
    async getDocument(projectId, documentId) {
      const response = await client.get(`/projects/${projectId}/documents/${documentId}`);
      return response.data;
    },
    async getDocumentHistory(projectId, documentId) {
      const response = await client.get(`/projects/${projectId}/documents/${documentId}/history`);
      return response.data.events;
    },
    async startDocumentUpload(projectId, input) {
      const response = await client.post(`/projects/${projectId}/documents/upload-session`, input);
      return response.data;
    },
    async startDocumentVersionUpload(projectId, documentId, input) {
      const response = await client.post(
        `/projects/${projectId}/documents/${documentId}/versions/upload-session`,
        input,
      );
      return response.data;
    },
    async uploadDocumentChunk(projectId, token, offset, chunk, isFinal) {
      const response = await client.post(`/projects/${projectId}/documents/uploads/chunk`, chunk, {
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Document-Upload-Token': token,
          'X-Upload-Offset': String(offset),
          'X-Upload-Final': isFinal ? '1' : '0',
        },
      });
      return response.data;
    },
    async updateDocument(projectId, documentId, input) {
      const response = await client.patch(`/projects/${projectId}/documents/${documentId}`, input);
      return response.data.document;
    },
    async setDocumentArchived(projectId, documentId, archived) {
      const response = await client.patch(
        `/projects/${projectId}/documents/${documentId}/archive`,
        { archived },
      );
      return response.data.document;
    },
    async openDocumentVersion(projectId, documentId, versionId) {
      const response = await client.get(
        `/projects/${projectId}/documents/${documentId}/versions/${versionId}/open`,
      );
      return { ...response.data, url: resolveApiFileUrl(response.data.url, PROJECT_COORDINATION_API_BASE_URL) };
    },
    async getDrawingSets(projectId, filters) {
      const response = await client.get(`/projects/${projectId}/drawing-sets`, { params: filters });
      return response.data.drawing_sets;
    },
    async getDrawingSet(projectId, setId) {
      const response = await client.get(`/projects/${projectId}/drawing-sets/${setId}`);
      return { drawingSet: response.data.drawingSet, pages: response.data.pages };
    },
    async openDrawingSet(projectId, setId) {
      const response = await client.get(`/projects/${projectId}/drawing-sets/${setId}/open`);
      return {
        ...response.data,
        viewer_url: coordinationContentUrl(`/projects/${projectId}/drawing-sets/${setId}/content`),
      };
    },
    async getDrawingSetDelivery(projectId, setId, options) {
      const response = await client.get<ProjectDrawingDelivery>(
        `/projects/${projectId}/drawing-sets/${setId}/delivery`,
        { signal: options?.signal },
      );
      return response.data;
    },
    async getDrawingSetHistory(projectId, setId) {
      const response = await client.get(`/projects/${projectId}/drawing-sets/${setId}/history`);
      return response.data.events;
    },
    async startDrawingSetUpload(projectId, input) {
      const response = await client.post(`/projects/${projectId}/drawing-sets/upload-session`, input);
      return response.data;
    },
    async uploadDrawingSetChunk(projectId, token, offset, chunk, isFinal) {
      const response = await client.post(`/projects/${projectId}/drawing-sets/uploads/chunk`, chunk, {
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Drawing-Upload-Token': token,
          'X-Upload-Offset': String(offset),
          'X-Upload-Final': isFinal ? '1' : '0',
        },
      });
      return response.data;
    },
    async setDrawingSetArchived(projectId, setId, archived) {
      const response = await client.patch(`/projects/${projectId}/drawing-sets/${setId}/archive`, { archived });
      return response.data.drawing_set;
    },
    async publishDrawingSetSource(projectId, setId) {
      const response = await client.post(`/projects/${projectId}/drawing-sets/${setId}/publish-source`);
      return response.data.drawing_set;
    },
    async deleteDrawingSetPermanently(projectId, setId) {
      const response = await client.delete(`/projects/${projectId}/drawing-sets/${setId}/permanent`);
      return response.data;
    },
    async setDrawingSetPageSkipped(projectId, setId, pageNumber, skipped) {
      const response = await client.patch(
        `/projects/${projectId}/drawing-sets/${setId}/pages/${pageNumber}/skipped`,
        { skipped },
      );
      return response.data.page;
    },
    async classifyDrawingSetPage(projectId, setId, pageNumber, input) {
      const response = await client.post(
        `/projects/${projectId}/drawing-sets/${setId}/pages/${pageNumber}/classify`,
        input,
      );
      return response.data;
    },
    async reviewDrawingSetPageMatch(projectId, setId, pageNumber, decision) {
      const response = await client.patch(
        `/projects/${projectId}/drawing-sets/${setId}/pages/${pageNumber}/match-review`,
        { decision },
      );
      return response.data.page;
    },
    async recognizeDrawingSetPage(projectId, setId, pageNumber, image, signal) {
      const response = await client.post(`/projects/${projectId}/drawing-sets/${setId}/pages/${pageNumber}/recognize`, image, {
        headers: { 'Content-Type': 'image/jpeg' }, signal, timeout: 55_000,
      });
      return response.data;
    },
    async batchDrawingSetPages(projectId, setId, input) {
      const response = await client.post(`/projects/${projectId}/drawing-sets/${setId}/pages/batch`, input, { timeout: 120_000 });
      return response.data;
    },
    async getDrawings(projectId, filters, options) {
      const response = await client.get(`/projects/${projectId}/drawings`, { params: filters, signal: options?.signal });
      return response.data.drawings;
    },
    async getDrawing(projectId, drawingId) {
      const response = await client.get(`/projects/${projectId}/drawings/${drawingId}`);
      return response.data;
    },
    async getDrawingHistory(projectId, drawingId) {
      const response = await client.get(`/projects/${projectId}/drawings/${drawingId}/history`);
      return response.data.events;
    },
    async getDrawingViewerBootstrap(projectId, drawingId, revisionId, options) {
      const response = await client.get(`/projects/${projectId}/drawings/${drawingId}/viewer`, {
        params: { revision_id: revisionId },
        signal: options?.signal,
      });
      return response.data;
    },
    async getDrawingRevisionDelivery(projectId, drawingId, revisionId, preferDerived, options) {
      const response = await client.get(
        `/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/delivery`,
        {
          params: { prefer_derived: preferDerived ? 'true' : undefined },
          signal: options?.signal,
        },
      );
      return response.data;
    },
    async getDrawingOfflineManifest(projectId, drawingId, revisionId) {
      const response = await client.get(
        `/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/offline-manifest`,
      );
      return response.data;
    },
    async listDrawingOfflineManifest(projectId, drawingSetId) {
      const response = await client.get(`/projects/${projectId}/drawings/offline-manifest`, {
        params: { drawing_set_id: drawingSetId },
      });
      return response.data;
    },
    async getDrawingOfflineDelivery(projectId, drawingId, revisionId, options) {
      const response = await client.get(
        `/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/offline-delivery`,
        { signal: options?.signal },
      );
      return response.data;
    },
    async getDrawingRevisionThumbnail(projectId, drawingId, revisionId, options) {
      const response = await client.get(
        `/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/thumbnail`,
        { responseType: 'blob', signal: options?.signal },
      );
      return response.data;
    },
    async optimizeDrawingRevision(projectId, drawingId, revisionId) {
      const response = await client.post(
        `/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/optimize`,
      );
      return response.data;
    },
    async uploadDrawingRevisionThumbnail(projectId, drawingId, revisionId, thumbnail) {
      const response = await client.post(
        `/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/thumbnail`,
        thumbnail,
        { headers: { 'Content-Type': 'image/jpeg' } },
      );
      return response.data;
    },
    async setDrawingArchived(projectId, drawingId, archived) {
      const response = await client.patch(`/projects/${projectId}/drawings/${drawingId}/archive`, { archived });
      return response.data.drawing;
    },
    async deleteDrawingPermanently(projectId, drawingId) {
      const response = await client.delete(`/projects/${projectId}/drawings/${drawingId}/permanent`);
      return response.data;
    },
    async publishDrawingRevision(projectId, drawingId, revisionId) {
      const response = await client.post(
        `/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/publish`,
      );
      return response.data;
    },
    async openDrawingRevision(projectId, drawingId, revisionId, preferDerived) {
      const response = await client.get(
        `/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/open`,
        { params: { prefer_derived: preferDerived ? 'true' : undefined } },
      );
      const derivedQuery = preferDerived ? '?prefer_derived=true' : '';
      return {
        ...response.data,
        viewer_url: coordinationContentUrl(`/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/content${derivedQuery}`),
      };
    },
    async getDrawingAnnotations(projectId, revisionId) {
      const response = await client.get(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations`);
      return response.data.annotations;
    },
    async createDrawingAnnotation(projectId, revisionId, input) {
      const response = await client.post(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations`, input);
      return response.data.annotation;
    },
    async updateDrawingAnnotation(projectId, revisionId, annotationId, input) {
      const response = await client.patch(
        `/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}`,
        input,
      );
      return response.data.annotation;
    },
    async deleteDrawingAnnotation(projectId, revisionId, annotationId) {
      await client.delete(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}`);
    },
    async setDrawingAnnotationStatus(projectId, revisionId, annotationId, status) {
      const response = await client.patch(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}/status`, { status });
      return response.data.annotation;
    },
    async reviewDrawingAnnotation(projectId, revisionId, annotationId, decision, point) {
      const response = await client.patch(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}/review`, { decision, ...point });
      return response.data.annotation;
    },
    async getDrawingAnnotationThread(projectId, revisionId, annotationId) {
      const response = await client.get(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}/messages`);
      return response.data;
    },
    async createDrawingAnnotationMessage(projectId, revisionId, annotationId, body) {
      const response = await client.post(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}/messages`, { body });
      return response.data.message;
    },
    async startDrawingAnnotationAttachmentUpload(projectId, revisionId, annotationId, input) {
      const response = await client.post(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}/attachments/upload-session`, input);
      return response.data;
    },
    async uploadDrawingAnnotationAttachmentChunk(projectId, revisionId, annotationId, token, offset, chunk, isFinal) {
      const response = await client.post(
        `/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}/attachments/uploads/chunk`,
        chunk,
        { headers: { 'Content-Type': 'application/octet-stream', 'X-Drawing-Annotation-Attachment-Upload-Token': token, 'X-Upload-Offset': String(offset), 'X-Upload-Final': isFinal ? '1' : '0' } },
      );
      return response.data;
    },
    async openDrawingAnnotationAttachment(projectId, revisionId, annotationId, attachmentId) {
      const response = await client.get(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}/attachments/${attachmentId}/open`);
      return response.data;
    },
    async deleteDrawingAnnotationAttachment(projectId, revisionId, annotationId, attachmentId) {
      await client.delete(`/projects/${projectId}/drawing-revisions/${revisionId}/annotations/${annotationId}/attachments/${attachmentId}`);
    },
    async getDrawingMarkups(projectId, revisionId) {
      const response = await client.get(`/projects/${projectId}/drawing-revisions/${revisionId}/markups`);
      return response.data.markups;
    },
    async createDrawingMarkup(projectId, revisionId, input) {
      const response = await client.post(`/projects/${projectId}/drawing-revisions/${revisionId}/markups`, input);
      return response.data.markup;
    },
    async updateDrawingMarkup(projectId, revisionId, markupId, input) {
      const response = await client.patch(`/projects/${projectId}/drawing-revisions/${revisionId}/markups/${markupId}`, input);
      return response.data.markup;
    },
    async deleteDrawingMarkup(projectId, revisionId, markupId, expectedUpdatedAt) {
      await client.delete(`/projects/${projectId}/drawing-revisions/${revisionId}/markups/${markupId}`, {
        data: { expected_updated_at: expectedUpdatedAt },
      });
    },
    async postDrawingViewerTelemetry(events) {
      await client.post('/drawings/telemetry', { events });
    },
    async getRfis(projectId, filters) {
      const response = await client.get(`/projects/${projectId}/rfis`, { params: filters });
      return response.data.rfis;
    },
    async getRfi(projectId, rfiId) {
      const response = await client.get(`/projects/${projectId}/rfis/${rfiId}`);
      return response.data;
    },
    async getRfiAssignableAdmins(projectId) {
      const response = await client.get(`/projects/${projectId}/rfis/assignable-admins`);
      return response.data.admins;
    },
    async createRfiDraft(projectId, input) {
      const response = await client.post(`/projects/${projectId}/rfis`, input);
      return response.data.rfi;
    },
    async updateRfiDraft(projectId, rfiId, input) {
      const response = await client.patch(`/projects/${projectId}/rfis/${rfiId}`, input);
      return response.data.rfi;
    },
    async updateRfiManagement(projectId, rfiId, input) {
      const response = await client.patch(`/projects/${projectId}/rfis/${rfiId}/management`, input);
      return response.data.rfi;
    },
    async openRfi(projectId, rfiId) {
      const response = await client.post(`/projects/${projectId}/rfis/${rfiId}/open`);
      return response.data.rfi;
    },
    async answerRfi(projectId, rfiId, responseText) {
      const response = await client.post(`/projects/${projectId}/rfis/${rfiId}/answer`, { response: responseText });
      return response.data.rfi;
    },
    async closeRfi(projectId, rfiId) {
      const response = await client.post(`/projects/${projectId}/rfis/${rfiId}/close`);
      return response.data.rfi;
    },
    async reopenRfi(projectId, rfiId) {
      const response = await client.post(`/projects/${projectId}/rfis/${rfiId}/reopen`);
      return response.data.rfi;
    },
    async voidRfi(projectId, rfiId, reason) {
      const response = await client.post(`/projects/${projectId}/rfis/${rfiId}/void`, { reason });
      return response.data.rfi;
    },
    async deleteRfiDraft(projectId, rfiId) {
      await client.delete(`/projects/${projectId}/rfis/${rfiId}`);
    },
    async startRfiAttachmentUpload(projectId, rfiId, input) {
      const response = await client.post(`/projects/${projectId}/rfis/${rfiId}/attachments/upload-session`, input);
      return response.data;
    },
    async uploadRfiAttachmentChunk(projectId, rfiId, token, offset, chunk, isFinal) {
      const response = await client.post(`/projects/${projectId}/rfis/${rfiId}/attachments/uploads/chunk`, chunk, {
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-RFI-Attachment-Upload-Token': token,
          'X-Upload-Offset': String(offset),
          'X-Upload-Final': isFinal ? '1' : '0',
        },
      });
      return response.data;
    },
    async openRfiAttachment(projectId, rfiId, attachmentId) {
      const response = await client.get(`/projects/${projectId}/rfis/${rfiId}/attachments/${attachmentId}/open`);
      return { ...response.data, url: resolveApiFileUrl(response.data.url, PROJECT_COORDINATION_API_BASE_URL) };
    },
    async deleteRfiAttachment(projectId, rfiId, attachmentId, role) {
      await client.delete(`/projects/${projectId}/rfis/${rfiId}/attachments/${attachmentId}`, {
        data: { attachment_role: role },
      });
    },
  };
}
